import React, { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { formatDate } from '@/lib/utils';
import { formatApiError } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

const WEGE = [
  { value: 'email', label: 'E-Mail' },
  { value: 'letter', label: 'Brief' },
  { value: 'pdf', label: 'PDF' },
];

function wegLabel(code) {
  const hit = WEGE.find((row) => row.value === code);
  return hit ? hit.label : (code || '–');
}

/**
 * Schreiben, die von dieser Belegung aus gingen: Datum, Betreff, Weg, Empfänger.
 * Eine neue Nachricht landet in derselben Liste.
 */
export default function EnrollmentCorrespondence({ enrollmentId }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [reading, setReading] = useState(null);
  const [draft, setDraft] = useState({ subject: '', recipient: '', channel: 'email', body: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!enrollmentId) return;
    try {
      const r = await api.get(`/enrollments/${enrollmentId}/correspondence`);
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (error) {
      toast.error(formatApiError(error, 'Korrespondenz konnte nicht geladen werden'));
      setRows([]);
    }
  }, [enrollmentId]);

  useEffect(() => { load(); }, [load]);

  const speichern = async () => {
    if (!draft.subject.trim() || !draft.recipient.trim() || saving) return;
    setSaving(true);
    try {
      await api.post(`/enrollments/${enrollmentId}/correspondence`, {
        subject: draft.subject.trim(),
        recipient: draft.recipient.trim(),
        channel: draft.channel,
        body: draft.body,
      });
      setOpen(false);
      setDraft({ subject: '', recipient: '', channel: 'email', body: '' });
      await load();
    } catch (error) {
      toast.error(formatApiError(error, 'Nachricht konnte nicht gespeichert werden'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-2 shrink-0" data-testid="uv-korrespondenz">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Korrespondenz</div>
        <button
          type="button"
          onClick={() => { setReading(null); setOpen(true); }}
          className="p-1 rounded hover:bg-slate-100 text-slate-500"
          title="Neue Nachricht"
          aria-label="Neue Nachricht"
          data-testid="uv-korrespondenz-neu"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-slate-400">Keine Korrespondenz zu dieser Belegung</p>
      ) : (
        <ul className="space-y-1">
          {rows.map((row) => (
            <li key={row.id}>
              <button
                type="button"
                className="w-full text-left text-xs rounded px-1 py-0.5 hover:bg-slate-50"
                data-testid={`uv-korrespondenz-row-${row.id}`}
                onClick={() => { setReading(row); setOpen(true); }}
              >
                <span className="text-slate-500">{row.sent_at ? formatDate(row.sent_at) : '–'}</span>
                {' · '}
                <span className="font-medium text-slate-800">{row.subject}</span>
                {' · '}
                <span>{row.channel_label || wegLabel(row.channel)}</span>
                {' · '}
                <span className="text-slate-600">{row.recipient}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="uv-korrespondenz-form">
          <DialogHeader>
            <DialogTitle>{reading ? 'Korrespondenz' : 'Neue Nachricht'}</DialogTitle>
          </DialogHeader>
          {reading ? (
            <div className="text-sm space-y-2">
              <div><span className="text-slate-500">Betreff </span>{reading.subject}</div>
              <div><span className="text-slate-500">Empfänger </span>{reading.recipient}</div>
              <div><span className="text-slate-500">Weg </span>{reading.channel_label || wegLabel(reading.channel)}</div>
              {reading.body ? <p className="whitespace-pre-wrap text-slate-700">{reading.body}</p> : null}
            </div>
          ) : (
            <div className="space-y-2">
              <Input
                placeholder="Betreff"
                value={draft.subject}
                onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                data-testid="uv-korrespondenz-subject"
              />
              <Input
                placeholder="Empfänger"
                value={draft.recipient}
                onChange={(e) => setDraft({ ...draft, recipient: e.target.value })}
                data-testid="uv-korrespondenz-recipient"
              />
              <select
                value={draft.channel}
                onChange={(e) => setDraft({ ...draft, channel: e.target.value })}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                data-testid="uv-korrespondenz-channel"
              >
                {WEGE.map((weg) => (
                  <option key={weg.value} value={weg.value}>{weg.label}</option>
                ))}
              </select>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Schließen</Button>
            {!reading && (
              <Button type="button" onClick={speichern} disabled={saving} data-testid="uv-korrespondenz-save">
                Speichern
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
